import { useParams, RouteComponentProps } from '@reach/router';
import { addDays, format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { uploadBlobs, describeUploadError } from '../../utils/blobUpload';
import 'react-datepicker/dist/react-datepicker.css';
import Button from '../../components/Button';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import Table, { TableData, TableHeader, TableRow } from '../../components/Table';
import { Tab, TabsContainer } from '../../components/Tabs';
import { useUser } from '../../queries/Accounts';
import { UpsertCallOffWithoutOrderIdDTO, CallOffItem, CallOff } from '../../queries/Calloff';
import { Delivery, DeliveryDTO, DeliveryItem } from '../../queries/Delivery';
import {
    Order,
    useApproveOrder,
    useCancelOrder,
    useCreateOrderCallOff,
    useCreateOrderDelivery,
    useGetOrder,
    useSendOrder,
} from '../../queries/Orders';
import { RequisitionItem } from '../../queries/Requisitions';
import {
    formatCurrencyAmount,
    handleWebDownload,
    validateEmail,
    fomatCurrencySymbol,
    datePickerInputClassName,
} from '../../utils/Utils';
import { domain } from '../../d-man';
import TypeAhead from '../../components/TypeAhead';
import { useGetUsers } from '../../queries/Users';
import { useGetPermissions } from '../../queries/Permissions';
import Select from '../../components/Select';
import { ProjectAddress } from '../../queries/Projects';
import TextArea from '../../components/TextArea';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import StatusBadge, { StatusTone } from '../../components/StatusBadge';
//

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function OrderDetail(props: RouteComponentProps) {
    const { navigate } = props;
    // CONSTANTS - TODO: Move!
    const DECIMAL_PLACES = 3;

    const user = useUser();

    const MAX_USERS = 10000;
    const users = useGetUsers(0, MAX_USERS);

    const params = useParams();
    const getId = () => params.id;

    const order = useGetOrder(getId());
    const orderApprove = useApproveOrder(getId());
    const orderCancel = useCancelOrder(getId());

    const [deliveryErrorMessage, setDeliveryErrorMessage] = useState('');
    const [callOffErrorMessage, setCallOffErrorMessage] = useState('');
    const [sendOrderErrorMessage, setSendOrderErrorMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [approvingOrder, setApprovingOrder] = useState(false);

    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [openSendOrderModal, setOpenSendOrderModal] = useState(false);
    const [openDownloadOrderModal, setOpenDownloadOrderModal] = useState(false);
    const [openCreateDeliveryModal, setOpenCreateDeliveryModal] = useState(false);
    const [openCreateCallOffModal, setOpenCreateCallOffModal] = useState(false);
    const [tabs, setTabs] = useState([] as Tab[]);
    const [selectedTab, setSelectedTab] = useState('');

    const [deliveryDate, setDeliveryDate] = useState(new Date());
    const [deliveryNote, setDeliveryNote] = useState('');
    const [deliveryFiles, setDeliveryFiles] = useState([] as File[]);
    const [returnFiles, setReturnFiles] = useState([] as File[]);
    const [deliveryItems, setDeliveryItems] = useState([] as DeliveryItem[]);
    const [email, setEmail] = useState('');
    const [returnNote, setReturnNote] = useState('');
    const [payByDate, setPayByDate] = useState(new Date() as Date | undefined);

    const [callOffDeliveryDate, setCallOffDeliveryDate] = useState(addDays(new Date(), 1));
    const [callOffNote, setCallOffNote] = useState('');
    const [callOffItems, setCallOffItems] = useState([] as CallOffItem[]);

    const [deliveryAddressId, setDeliveryAddressId] = useState('');

    const orderDeliveryCreate = useCreateOrderDelivery(getId());
    const callOffCreate = useCreateOrderCallOff(getId());
    const sendOrder = useSendOrder(getId());

    const getPermissions = useGetPermissions('ORDER', getId());

    const formatEmails = () => {
        if (users && users.data && users.data?.length > 0) {
            return users.data.map((theUser: { email: string }) => ({
                id: theUser.email,
                name: theUser.email,
            }));
        }
        return [];
    };

    const usersRef = useRef(users);

    useEffect(() => {
        usersRef.current.reset();
    }, []);

    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Order_Show_Order_Details_screen')
            ?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        if (order.data && getPermissions.data) {
            const { type, lineItems } = order.data;
            if (tabs.length === 0) {
                if (
                    !getPermissions.data?.items.find((p) => p.code === 'Order_Show_Deliveries_Tab')
                        ?.hasAccess
                ) {
                    setTabs([{ label: 'Materials', id: 'Materials' }]);
                } else if (type === 'SINGLE') {
                    // single order
                    setTabs([
                        { label: 'Materials', id: 'Materials' },
                        { label: 'Deliveries', id: 'Deliveries' },
                    ]);
                } else {
                    setTabs([
                        { label: 'Materials', id: 'Materials' },
                        { label: 'Call-Offs', id: 'Call-Offs' },
                    ]);
                }
                setSelectedTab('Materials');
            }

            if (lineItems) {
                if (deliveryItems.length === 0) {
                    setDeliveryItems(
                        lineItems.map(
                            (i: RequisitionItem) =>
                                ({
                                    lineItemId: i.id,
                                    quantity: 0,
                                    returnedQuantity: 0,
                                } as DeliveryItem)
                        )
                    );
                }

                if (type === 'BULK' && callOffItems.length === 0) {
                    setCallOffItems(
                        lineItems.map(
                            (i: RequisitionItem) =>
                                ({
                                    lineItemId: i.id,
                                    quantity: 0,
                                } as CallOffItem)
                        )
                    );
                }
            }
        }
    }, [tabs, order.data, deliveryItems, callOffItems, user, getPermissions.data]);

    const getStatus = (orderStatus: Order) => {
        if (orderStatus.cancelledAt !== null || orderStatus.cancelledBy !== null) {
            return 'Cancelled';
        }
        if (orderStatus.exportedAt !== null || orderStatus.exportedBy !== null) {
            return 'Exported';
        }
        if (orderStatus.approvedAt !== null || orderStatus.approvedBy !== null) {
            return 'Approved';
        }
        return 'Draft';
    };

    const getStatusTone = (orderStatus: Order): StatusTone => {
        if (orderStatus.cancelledAt !== null || orderStatus.cancelledBy !== null) {
            return 'danger';
        }
        if (orderStatus.exportedAt !== null || orderStatus.exportedBy !== null) {
            return 'success';
        }
        if (orderStatus.approvedAt !== null || orderStatus.approvedBy !== null) {
            return 'info';
        }
        return 'neutral';
    };

    const handleCancel = () => {
        setIsLoading(true);
        orderCancel.execute().then((result) => {
            if (!result[0]) {
                order.reset();
                if (props.navigate) {
                    props.navigate(`/orders`);
                }
            } else {
                setErrorMessage(result[0]);
            }
            setIsLoading(false);
        });
    };

    const getOrderTotal = (lineItems: RequisitionItem[]) => {
        return (
            lineItems &&
            `${formatCurrencyAmount(
                lineItems.reduce(
                    (a, b) => ({
                        displayAmount: a.displayAmount + (b.quantity || 0) * (b.buyOutRate || 0),
                    }),
                    {
                        displayAmount: 0,
                    }
                ).displayAmount || 0
            )}`
        );
    };

    const getOrderRawTotal = (lineItems: RequisitionItem[]) => {
        return (
            (lineItems &&
                lineItems.reduce(
                    (a, b) => ({
                        displayAmount: a.displayAmount + (b.quantity || 0) * (b.buyOutRate || 0),
                    }),
                    {
                        displayAmount: 0,
                    }
                ).displayAmount) ||
            0
        );
    };

    const getVatTotal = (lineItems: RequisitionItem[]) => {
        const vat = lineItems[0].vat || 0;
        const vatTotal = getOrderRawTotal(lineItems || []) * (vat / 100);
        return parseFloat(vatTotal.toFixed(2));
    };

    const getTotalAmount = (subTotal: number, vatTotal: number) => {
        return formatCurrencyAmount(parseFloat((subTotal + vatTotal).toFixed(2)));
    };

    const getLineItemNumber = (row: RequisitionItem) => {
        if (!row?.id) return '';
        const lineItemIndex = order?.data?.lineItems?.findIndex((item) => item.id === row.id);
        return !lineItemIndex || lineItemIndex === -1 ? 1 : lineItemIndex + 1;
    };

    const handleCreateDeliveryModalOpen = () => {
        setOpenCreateDeliveryModal(true);
    };

    const handleCreateDeliveryModalClose = () => {
        setOpenCreateDeliveryModal(false);
    };

    const handleCreateCallOffModalOpen = () => {
        setOpenCreateCallOffModal(true);
    };

    const handleCreateCallOffModalClose = () => {
        setOpenCreateCallOffModal(false);
    };

    const handleSendOrderModalOpen = () => {
        setOpenSendOrderModal(true);
    };

    const handleSendOrderModalClose = () => {
        setOpenSendOrderModal(false);
    };

    const round = (value: number, decimals: number) => {
        return Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);
    };

    const postCreateDelivery = async () => {
        if (
            !deliveryDate ||
            !deliveryNote ||
            !payByDate ||
            (deliveryItems.every((i) => i.returnedQuantity <= 0) &&
                deliveryItems.every((i) => i.quantity <= 0))
        ) {
            setDeliveryErrorMessage('Please capture all the fields below');
        } else if (
            deliveryItems.some((i) => i.returnedQuantity) &&
            (returnFiles.length <= 0 || !returnNote)
        ) {
            setDeliveryErrorMessage('Please capture all the fields below');
        } else if (deliveryFiles.length <= 0) {
            setDeliveryErrorMessage('You must upload at least 1 file');
        } else if (order.data) {
            setIsLoading(true);
            try {
                const blobs = await uploadBlobs(deliveryFiles);
                const returnBlobs = await uploadBlobs(returnFiles);

                const data: DeliveryDTO = {
                    deliveredAt: deliveryDate,
                    note: deliveryNote,
                    items: deliveryItems.filter((i) => i.quantity > 0 || i.returnedQuantity > 0),
                    blobs,
                    returnNote,
                    returnBlobs,
                    payByDate,
                };

                const [error] = await orderDeliveryCreate.execute(data);
                if (error) {
                    setDeliveryErrorMessage(error);
                    return;
                }

                order.reset();
            } catch (error) {
                // Nothing caught these before, so a failed upload left the
                // dialog spinning with no message and no way out but a reload.
                setDeliveryErrorMessage(describeUploadError(error));
            } finally {
                // In a finally so every path clears it, including the failures.
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (orderDeliveryCreate.error) {
            setDeliveryErrorMessage(orderDeliveryCreate.error);
        }
    }, [orderDeliveryCreate.error]);

    useEffect(() => {
        handleCreateDeliveryModalClose();
    }, [orderDeliveryCreate.data]);

    const postCreateCallOff = async () => {
        if (!callOffDeliveryDate || callOffItems.every((i) => i.quantity <= 0)) {
            setCallOffErrorMessage('Please capture all the fields above');
        } else {
            setIsLoading(true);
            const data: UpsertCallOffWithoutOrderIdDTO = {
                requiredAt: format(callOffDeliveryDate, 'yyyy-MM-dd'),
                description: callOffNote,
                items: callOffItems.filter((i) => i.quantity > 0),
            };
            await callOffCreate.execute(data);
            setIsLoading(false);
            order.reset();
        }
    };

    useEffect(() => {
        if (callOffCreate.error) {
            setCallOffErrorMessage(callOffCreate.error);
        }
    }, [callOffCreate.error]);

    useEffect(() => {
        handleCreateCallOffModalClose();
    }, [callOffCreate.data]);

    useEffect(() => {
        if (
            order.data?.requisition?.project?.addresses &&
            order.data?.requisition?.project?.addresses?.length
        ) {
            setDeliveryAddressId(order.data?.requisition?.project?.addresses[0].id || '');
        }
    }, [order.data]);

    const postSendOrder = async () => {
        if (!email) {
            setSendOrderErrorMessage('Please capture all the fields above');
        } else if (!validateEmail(email)) {
            setSendOrderErrorMessage('Please provide a valid email address');
        } else {
            setIsLoading(true);
            await sendOrder.execute({
                email,
                deliveryAddressId,
            });
            // I cant get this error to work!
            if (sendOrder.error) {
                // Display create order error here
            } else {
                handleSendOrderModalClose();
            }
            order.reset();
            setIsLoading(false);
        }
    };

    const handleDownload = async (endpoint: string) => {
        setIsLoading(true);
        const [, data, response] = await domain
            .get(endpoint, {
                requestConfig: {
                    responseType: 'blob', // important
                },
            })
            .execute();
        setIsLoading(false);
        if (!data) return;

        handleWebDownload(data, response);
        order.reset();
    };

    const postDownloadOrder = async () => {
        await handleDownload(
            `/orders/${order.data?.id}/download?deliveryAddressId=${deliveryAddressId}`
        );
        setOpenDownloadOrderModal(false);
    };

    const updateUploadedFiles = (files: File[]) => {
        setDeliveryFiles(files);
    };

    const updateUploadedFile = (files: File[]) => {
        setReturnFiles(files);
    };

    const toCallOffDetail = (id?: string) => {
        if (props.navigate) {
            props.navigate(`/calloffs/${id}`);
        }
    };

    const toDeliveryDetail = (id?: string) => {
        if (props.navigate) {
            props.navigate(`/deliveries/${id}`);
        }
    };

    const toRequisitionDetail = (id?: string) => {
        if (props.navigate) {
            props.navigate(`/requisitions/${id}`);
        }
    };

    const formatDeliveryAddresses = () => {
        if (order.data && order.data.requisition?.project?.addresses) {
            const filteredAddresses = order.data.requisition?.project?.addresses.map(
                (address: ProjectAddress, i) =>
                    ({
                        value: address.id,
                        label: `Address ${i + 1}`,
                    } as { value: string; label: string })
            );
            return filteredAddresses;
        }
        return [];
    };

    const formatProjectAddressSelections = () => {
        if (order?.data?.requisition && order.data?.requisition?.project?.addresses) {
            const filteredAddresses = order?.data?.requisition.project?.addresses.map(
                (address: ProjectAddress) =>
                    ({
                        value: address.id,
                        label: `${address.address1} ${address.address2} ${address.address3}`,
                    } as { value: string; label: string })
            );
            return filteredAddresses;
        }
        return [];
    };

    const formatCreditorAddress = () => {
        if (order.data && order.data.lineItems && order.data.lineItems[0]?.creditor) {
            const { creditor } = order.data.lineItems[0];
            return `${creditor.address1}, ${creditor.address2}, ${creditor.address3}`;
        }
        return '';
    };

    const getDeliveryAddressDetails = () => {
        if (order.data && order.data.requisition?.project?.addresses && deliveryAddressId) {
            const filteredAddress = order.data.requisition?.project?.addresses.find(
                (i) => i.id === deliveryAddressId
            );
            const addressLines = [];
            if (filteredAddress?.address1) {
                addressLines.push(filteredAddress?.address1);
            }
            if (filteredAddress?.address2) {
                addressLines.push(filteredAddress?.address2);
            }
            if (filteredAddress?.address3) {
                addressLines.push(filteredAddress?.address3);
            }
            if (filteredAddress?.postCode) {
                addressLines.push(filteredAddress?.postCode);
            }
            return addressLines.join('\n') || '';
        }
        return '';
    };

    const getUserFullName = (userId: string) => {
        const chosenUser =
            userId &&
            users.data?.find((userItem) => userItem.id?.toUpperCase() === userId.toUpperCase());
        if (chosenUser) {
            return `${chosenUser.firstName} ${chosenUser.lastName}`;
        }
        return '';
    };

    const renderOrder = () => {
        if (!order || order.loading) {
            return <Loader message="Fetching Order from server" />;
        }
        if (order.error) {
            return <Error message={`Failed to fetch Order from the server (${order.error})`} />;
        }
        if (order.data) {
            const {
                number,
                createdAt,
                type,
                requisition,
                lineItems,
                approvedAt,
                approvedBy,
                requiredAt,
                cancelledAt,
                cancelledBy,
            } = order.data;

            return (
                <>
                    <div className="py-3 px-4 border-b border-border">
                        <div className="text-lg font-semibold text-foreground">
                            Order Information
                        </div>
                    </div>
                    <div className="p-3 pb-5">
                        <div className="w-full flex">
                            <div className="flex flex-col gap-1.5 w-1/4 pr-4 border-r border-border">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Created At
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {createdAt
                                            ? format(new Date(createdAt), 'yyyy-MM-dd HH:mm:ss')
                                            : ''}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Status
                                    </div>
                                    <div className="w-1/2">
                                        <StatusBadge
                                            label={getStatus(order.data)}
                                            tone={getStatusTone(order.data)}
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Order Type
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {type?.toString() || ''}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Job Code
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {requisition?.project?.jobCode}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Supplier Code
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {lineItems && lineItems[0].creditor?.code}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Order No.
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {`${number?.toString().padStart(6, '0')}`}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Req No.
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {`${requisition?.number?.toString().padStart(6, '0')}`}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Created By
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {getUserFullName(requisition?.createdBy || '')}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Approved By
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {getUserFullName(approvedBy || '')}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Approved At
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {approvedAt
                                            ? format(
                                                  new Date(approvedAt || ''),
                                                  'yyyy-MM-dd HH:mm:ss'
                                              )
                                            : ''}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium text-label w-1/2">
                                        Required At
                                    </div>
                                    <div className="w-1/2 text-sm text-foreground truncate">
                                        {requiredAt
                                            ? format(new Date(requiredAt || ''), 'yyyy-MM-dd')
                                            : ''}
                                    </div>
                                </div>
                                {cancelledAt && cancelledBy && (
                                    <>
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="text-sm font-medium text-label w-1/2">
                                                Cancelled By
                                            </div>
                                            <div className="w-1/2 text-sm text-foreground truncate">
                                                {getUserFullName(cancelledBy || '')}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="text-sm font-medium text-label w-1/2">
                                                Cancelled At
                                            </div>
                                            <div className="w-1/2 text-sm text-foreground truncate">
                                                {cancelledAt
                                                    ? format(
                                                          new Date(cancelledAt || ''),
                                                          'yyyy-MM-dd HH:mm:ss'
                                                      )
                                                    : ''}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex justify-between gap-6 pl-6 w-3/4">
                                <div className="flex flex-col gap-1.5 w-1/2">
                                    <span className="text-base font-semibold text-foreground">
                                        Creditor Details
                                    </span>
                                    <div className="border-t border-border" />
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Contact Person
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {(lineItems && lineItems[0].creditor?.contactPerson) ||
                                                ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Tel.
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {(lineItems && lineItems[0].creditor?.tel) || ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Cell.
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {(lineItems && lineItems[0].creditor?.cell) || ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Email
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {(lineItems && lineItems[0].creditor?.email) || ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Address
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {(lineItems && formatCreditorAddress()) || ''}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1.5 w-1/2">
                                    <span className="text-base font-semibold text-foreground">
                                        Project Details
                                    </span>
                                    <div className="border-t border-border" />
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Contact Person
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {requisition?.project?.contactPerson || ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Tel.
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {requisition?.project?.tel || ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Cell.
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {requisition?.project?.cell || ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-medium text-label w-1/4">
                                            Email
                                        </div>
                                        <div className="w-3/4 text-sm text-foreground truncate">
                                            {requisition?.project?.email || ''}
                                        </div>
                                    </div>
                                    {requisition?.project?.addresses && (
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="text-sm font-medium text-label w-1/4">
                                                {requisition?.project?.addresses.length > 1
                                                    ? 'Delivery Addresses'
                                                    : 'Delivery Address'}
                                            </div>
                                            <div className="w-3/4">
                                                <Select
                                                    options={formatProjectAddressSelections()}
                                                    selected={deliveryAddressId}
                                                    onChange={(event) =>
                                                        setDeliveryAddressId(event.target.value)
                                                    }
                                                    width="w-full"
                                                    compact
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            );
        }
        return null;
    };

    const renderOrderButtons = () => {
        if (order.data) {
            const { requisition } = order.data;
            return (
                <div className="flex space-x-4 p-4">
                    <div className="mb-2">
                        <Button onClick={() => toRequisitionDetail(requisition?.id)}>
                            Go To Requisition
                        </Button>
                    </div>

                    {getPermissions.data?.items.find((p) => p.code === 'Order_Approve')
                        ?.hasAccess &&
                        order.data.approvedAt === null &&
                        order.data.approvedBy === null &&
                        order.data.cancelledAt === null &&
                        order.data.cancelledBy === null && (
                            <div>
                                <Button
                                    onClick={() => {
                                        setApprovingOrder(true);
                                        orderApprove.execute().then(() => {
                                            setApprovingOrder(false);
                                            order.reset();
                                        });
                                    }}
                                    disabled={approvingOrder}
                                >
                                    {approvingOrder ? 'Loading' : 'Approve'}
                                </Button>
                            </div>
                        )}
                    {order.data &&
                        getPermissions.data?.items.find((p) => p.code === 'Order_Download')
                            ?.hasAccess && (
                            <div>
                                <Button onClick={() => setOpenDownloadOrderModal(true)}>
                                    Download Order
                                </Button>
                            </div>
                        )}
                    {order.data &&
                        getPermissions.data?.items.find((p) => p.code === 'Order_Send')
                            ?.hasAccess && (
                            <div>
                                <Button onClick={handleSendOrderModalOpen}>Send Order</Button>
                            </div>
                        )}
                    {((order.data.exportedAt == null &&
                        order.data.exportedBy == null &&
                        order.data.cancelledAt === null &&
                        order.data.cancelledBy === null &&
                        getPermissions.data?.items.find((p) => p.code === 'Order_Export')
                            ?.hasAccess) ||
                        getPermissions.data?.items.find((p) => p.code === 'Order_Export_Override')
                            ?.hasAccess) &&
                        order.data.approvedAt !== null &&
                        order.data.approvedBy !== null && (
                            <div>
                                <Button
                                    onClick={() =>
                                        handleDownload(`/orders/${order.data?.id}/export`)
                                    }
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Loading' : 'Export'}
                                </Button>
                            </div>
                        )}
                    {getPermissions.data?.items.find((p) => p.code === 'Order_Cancel')?.hasAccess &&
                        order.data &&
                        order.data.cancelledAt === null &&
                        order.data.cancelledBy === null && (
                            <div>
                                <Button onClick={() => setOpenCancelModal(true)}>
                                    Cancel Order
                                </Button>
                            </div>
                        )}
                </div>
            );
        }
        return <div />;
    };

    const getTotalDelivered = (reqItemParam: RequisitionItem, orderParam?: Order) => {
        if (
            orderParam &&
            orderParam.type === 'SINGLE' &&
            orderParam.orderDeliveries &&
            orderParam.orderDeliveries.length > 0
        ) {
            const { quantity } = orderParam.orderDeliveries
                .map((i) =>
                    i.items
                        .filter((j) => j.lineItemId === reqItemParam.id)
                        .reduce((a, b) => ({ lineItemId: '', quantity: a.quantity + b.quantity }), {
                            lineItemId: '',
                            quantity: 0,
                        })
                )
                .reduce((a, b) => ({ lineItemId: '', quantity: a.quantity + b.quantity }), {
                    lineItemId: '',
                    quantity: 0,
                });
            return round(quantity, DECIMAL_PLACES);
        }
        return 0;
    };

    const getTotalCalledOff = (reqItemParam: RequisitionItem, orderParam?: Order) => {
        if (orderParam && orderParam.callOffs && orderParam.callOffs.length > 0) {
            const { quantity } = orderParam.callOffs
                .map((i) =>
                    i.items
                        .filter((j) => j.lineItemId === reqItemParam.id)
                        .reduce((a, b) => ({ lineItemId: '', quantity: a.quantity + b.quantity }), {
                            lineItemId: '',
                            quantity: 0,
                        })
                )
                .reduce((a, b) => ({ lineItemId: '', quantity: a.quantity + b.quantity }), {
                    lineItemId: '',
                    quantity: 0,
                });
            return round(quantity, DECIMAL_PLACES);
        }
        return 0;
    };

    const checkDeliveriesAndCalloffs = () => {
        if (order.data) {
            if (
                (order.data?.callOffs && order.data.callOffs?.length > 0) ||
                (order.data?.orderDeliveries && order.data.orderDeliveries?.length > 0)
            ) {
                return (
                    <span className="text-sm text-danger">
                        [Warning: Order has existing deliveries/calloffs.]
                    </span>
                );
            }
        }
        return '';
    };

    return (
        <>
            {!getPermissions.data && <Loader message="Loading order..." />}
            {getPermissions.data && order.data && (
                <div className="flex flex-col">
                    <Modal
                        title="Cancel Order"
                        onClose={() => setOpenCancelModal(false)}
                        isOpen={openCancelModal}
                    >
                        <div className="w-72">
                            <div className="flex flex-col justify-between mt-5">
                                Are you sure you want to cancel this order?
                                {checkDeliveriesAndCalloffs()}
                            </div>
                            <div />
                            <div className="flex flex-row-reverse">
                                <div className="ml-3">
                                    <Button onClick={handleCancel} disabled={isLoading}>
                                        {isLoading ? 'Loading' : 'Yes'}
                                    </Button>
                                </div>
                                <div>
                                    <Button secondary onClick={() => setOpenCancelModal(false)}>
                                        No
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Modal>

                    <Modal
                        title="New Delivery"
                        // subtitle={`${order.data.number.toString().padStart(6, '0')}`}
                        onClose={handleCreateDeliveryModalClose}
                        isOpen={openCreateDeliveryModal}
                        onAction={postCreateDelivery}
                        actionText={isLoading ? 'Loading' : ''}
                    >
                        <div>
                            {deliveryErrorMessage && (
                                <div className="flex mb-5 text-sm text-danger font-semibold">
                                    {deliveryErrorMessage}
                                </div>
                            )}
                            <div className="flex justify-between">
                                <div>
                                    <div className="w-full h-full mb-5">
                                        <div className="text-sm font-medium text-label pb-1.5">
                                            Material Information
                                        </div>
                                        <table className="table-auto bg-surface w-full">
                                            <thead>
                                                <TableRow>
                                                    <TableHeader>No</TableHeader>
                                                    <TableHeader>Description</TableHeader>
                                                    <TableHeader>Cost Code</TableHeader>
                                                    <TableHeader>Creditor</TableHeader>
                                                    <TableHeader>Quantity Remaining</TableHeader>
                                                    <TableHeader>Delivered</TableHeader>
                                                    <TableHeader>Returned</TableHeader>
                                                </TableRow>
                                            </thead>
                                            <tbody>
                                                {order.data &&
                                                    order.data.lineItems &&
                                                    deliveryItems.length > 0 &&
                                                    order.data.lineItems.map(
                                                        (reqItem: RequisitionItem) => {
                                                            return (
                                                                <TableRow hover>
                                                                    <TableData>
                                                                        <div className="p-1">
                                                                            {getLineItemNumber(
                                                                                reqItem
                                                                            )}
                                                                        </div>
                                                                    </TableData>
                                                                    <TableData id={reqItem.id}>
                                                                        {reqItem.description}
                                                                    </TableData>
                                                                    <TableData>
                                                                        {reqItem.costCode &&
                                                                            reqItem.costCode.name}
                                                                    </TableData>
                                                                    <TableData>
                                                                        {reqItem.creditor?.name ||
                                                                            ''}
                                                                    </TableData>
                                                                    <TableData>
                                                                        {order.data?.type ===
                                                                            'SINGLE' &&
                                                                            reqItem.quantity &&
                                                                            round(
                                                                                reqItem.quantity -
                                                                                    getTotalDelivered(
                                                                                        reqItem,
                                                                                        order.data
                                                                                    ),
                                                                                DECIMAL_PLACES
                                                                            )}
                                                                    </TableData>
                                                                    <TableData>
                                                                        <Input
                                                                            value={deliveryItems
                                                                                .filter(
                                                                                    (i) =>
                                                                                        i.lineItemId ===
                                                                                        reqItem.id
                                                                                )[0]
                                                                                .quantity.toString()}
                                                                            onChange={(event) => {
                                                                                const oldDeliveryItem = deliveryItems.find(
                                                                                    (i) =>
                                                                                        i.lineItemId ===
                                                                                        reqItem.id
                                                                                );
                                                                                const newDeliveryItems = deliveryItems.filter(
                                                                                    (i) =>
                                                                                        i.lineItemId !==
                                                                                        reqItem.id
                                                                                );
                                                                                newDeliveryItems.push(
                                                                                    {
                                                                                        lineItemId:
                                                                                            reqItem.id,
                                                                                        quantity: parseFloat(
                                                                                            event
                                                                                                .target
                                                                                                .value
                                                                                        ),
                                                                                        returnedQuantity:
                                                                                            oldDeliveryItem?.returnedQuantity,
                                                                                    } as DeliveryItem
                                                                                );
                                                                                setDeliveryItems(
                                                                                    newDeliveryItems
                                                                                );
                                                                            }}
                                                                            type="number"
                                                                        />
                                                                    </TableData>
                                                                    <TableData>
                                                                        <Input
                                                                            value={deliveryItems
                                                                                .filter(
                                                                                    (i) =>
                                                                                        i.lineItemId ===
                                                                                        reqItem.id
                                                                                )[0]
                                                                                .returnedQuantity?.toString()}
                                                                            onChange={(event) => {
                                                                                const oldDeliveryItem = deliveryItems.find(
                                                                                    (i) =>
                                                                                        i.lineItemId ===
                                                                                        reqItem.id
                                                                                );
                                                                                const newDeliveryItems = deliveryItems.filter(
                                                                                    (i) =>
                                                                                        i.lineItemId !==
                                                                                        reqItem.id
                                                                                );
                                                                                newDeliveryItems.push(
                                                                                    {
                                                                                        lineItemId:
                                                                                            reqItem.id,
                                                                                        returnedQuantity: parseFloat(
                                                                                            event
                                                                                                .target
                                                                                                .value ||
                                                                                                '0'
                                                                                        ),
                                                                                        quantity:
                                                                                            oldDeliveryItem?.quantity,
                                                                                    } as DeliveryItem
                                                                                );
                                                                                setDeliveryItems(
                                                                                    newDeliveryItems
                                                                                );
                                                                            }}
                                                                            type="number"
                                                                        />
                                                                    </TableData>
                                                                </TableRow>
                                                            );
                                                        }
                                                    )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="ml-5">
                                    <div className="mb-5">
                                        <div className="text-sm font-medium text-label pb-1.5">
                                            Delivery Date
                                        </div>
                                        <div>
                                            <DatePicker
                                                dateFormat="yyyy-MM-dd"
                                                selected={deliveryDate}
                                                onChange={(date: Date) => setDeliveryDate(date)}
                                                className={datePickerInputClassName}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-5">
                                        <div className="text-sm font-medium text-label pb-1.5">
                                            Pay By
                                        </div>
                                        <DatePicker
                                            dateFormat="yyyy-MM-dd"
                                            selected={payByDate}
                                            onChange={(date: Date) => setPayByDate(date)}
                                            className={datePickerInputClassName}
                                        />
                                    </div>
                                    <div className="mb-5">
                                        <Input
                                            label="Delivery Note"
                                            placeholder="Add Note Here"
                                            value={deliveryNote}
                                            type="text"
                                            onChange={(event) =>
                                                setDeliveryNote(event.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="mb-5">
                                        <div className="text-sm font-medium text-label pb-1.5">
                                            Upload Documents
                                        </div>
                                        <FileUpload
                                            updateFilesCb={updateUploadedFiles}
                                            onError={(error) => setDeliveryErrorMessage(error)}
                                            // accept=".jpg,.png,.jpeg"
                                            multiple
                                        />
                                    </div>
                                    <div className="mb-5">
                                        <div className="text-sm font-medium text-label pb-1.5">
                                            Return (Optional)
                                        </div>
                                        <div className="border-t border-border mb-3" />
                                        <Input
                                            label="Return Note"
                                            placeholder="Add Note Here"
                                            value={returnNote}
                                            type="text"
                                            onChange={(event) => setReturnNote(event.target.value)}
                                        />
                                        <div className="mb-3" />
                                        <FileUpload
                                            multiple={false}
                                            updateFilesCb={updateUploadedFile}
                                            onError={(error) => setDeliveryErrorMessage(error)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal>
                    <Modal
                        title="New Call-Off"
                        // subtitle={`${order.data.number.toString().padStart(6, '0')}`}
                        onClose={handleCreateCallOffModalClose}
                        isOpen={openCreateCallOffModal}
                        onAction={postCreateCallOff}
                        actionText={isLoading ? 'Loading' : ''}
                    >
                        <div className="flex justify-between">
                            <div>
                                <div className="w-full h-full mb-5">
                                    <div className="text-sm font-medium text-label pb-1.5">
                                        Material Information
                                    </div>
                                    <table className="table-auto bg-surface w-full">
                                        <thead>
                                            <TableRow>
                                                <TableHeader>Description</TableHeader>
                                                <TableHeader>Cost Code</TableHeader>
                                                <TableHeader>Creditor</TableHeader>
                                                <TableHeader>Quantity Remaining</TableHeader>
                                                <TableHeader>Call-Off</TableHeader>
                                            </TableRow>
                                        </thead>
                                        <tbody>
                                            {order.data &&
                                                order.data.lineItems &&
                                                callOffItems.length > 0 &&
                                                order.data.lineItems.map(
                                                    (reqItem: RequisitionItem) => {
                                                        return (
                                                            <TableRow hover>
                                                                <TableData id={reqItem.id}>
                                                                    {reqItem.description}
                                                                </TableData>
                                                                <TableData>
                                                                    {reqItem.costCode &&
                                                                        reqItem.costCode.name}
                                                                </TableData>
                                                                <TableData>
                                                                    {reqItem.creditor?.name || ''}
                                                                </TableData>
                                                                <TableData>
                                                                    {reqItem.quantity &&
                                                                        round(
                                                                            reqItem.quantity -
                                                                                getTotalCalledOff(
                                                                                    reqItem,
                                                                                    order.data
                                                                                ),
                                                                            DECIMAL_PLACES
                                                                        )}
                                                                </TableData>
                                                                <TableData>
                                                                    <Input
                                                                        value={callOffItems
                                                                            .filter(
                                                                                (i) =>
                                                                                    i.lineItemId ===
                                                                                    reqItem.id
                                                                            )[0]
                                                                            .quantity.toString()}
                                                                        onChange={(event) => {
                                                                            const newCallOffItems = callOffItems.filter(
                                                                                (i) =>
                                                                                    i.lineItemId !==
                                                                                    reqItem.id
                                                                            );
                                                                            newCallOffItems.push({
                                                                                lineItemId:
                                                                                    reqItem.id,
                                                                                quantity: parseFloat(
                                                                                    event.target
                                                                                        .value
                                                                                ),
                                                                            } as CallOffItem);
                                                                            setCallOffItems(
                                                                                newCallOffItems
                                                                            );
                                                                        }}
                                                                        type="number"
                                                                    />
                                                                </TableData>
                                                            </TableRow>
                                                        );
                                                    }
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="ml-5">
                                <div className="mb-5">
                                    <div className="text-sm font-medium text-label pb-1.5">
                                        Delivery Date
                                    </div>
                                    <div>
                                        <DatePicker
                                            dateFormat="yyyy-MM-dd"
                                            selected={callOffDeliveryDate}
                                            onChange={(date: Date) => setCallOffDeliveryDate(date)}
                                            className={datePickerInputClassName}
                                        />
                                    </div>
                                </div>
                                <div className="mb-5">
                                    <Input
                                        label="Notes"
                                        placeholder="Add Notes Here"
                                        value={callOffNote}
                                        type="text"
                                        onChange={(event) => setCallOffNote(event.target.value)}
                                    />
                                </div>
                                <div className="flex mb-5 text-sm text-danger">
                                    {callOffErrorMessage}
                                </div>
                            </div>
                        </div>
                    </Modal>
                    <Modal
                        title="Download Order"
                        onClose={() => setOpenDownloadOrderModal(false)}
                        isOpen={openDownloadOrderModal}
                    >
                        <div className="flex justify-between">
                            <div>
                                <div className="mb-5">
                                    <Select
                                        label="Delivery Address"
                                        placeholder="Address 1"
                                        options={formatDeliveryAddresses()}
                                        selected={deliveryAddressId}
                                        onChange={(event) =>
                                            setDeliveryAddressId(event.target.value)
                                        }
                                    />
                                </div>
                                <div className="mb-5">
                                    <TextArea
                                        label=""
                                        value={getDeliveryAddressDetails()}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        <div />
                        <div className="flex mb-5 text-sm text-danger">{sendOrderErrorMessage}</div>
                        <div className="flex flex-row-reverse">
                            <div className="pl-2" />
                            <div>
                                <Button onClick={postDownloadOrder} disabled={isLoading}>
                                    {!isLoading ? 'Submit' : <div>Loading</div>}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                    <Modal
                        title="Send Order"
                        subtitle="Email Order to email address"
                        onClose={handleSendOrderModalClose}
                        isOpen={openSendOrderModal}
                    >
                        <div className="flex justify-between">
                            <div>
                                <div className="mb-5">
                                    <TypeAhead
                                        label="Email Address"
                                        value={email}
                                        options={formatEmails()}
                                        hideNoMatchMsg
                                        placeholder="Provide Email Address"
                                        onClick={(event) =>
                                            setEmail(
                                                ((event.target as unknown) as {
                                                    id: string;
                                                }).id
                                            )
                                        }
                                    />
                                </div>
                                <div className="mb-5">
                                    <Select
                                        label="Delivery Address"
                                        placeholder="Address 1"
                                        options={formatDeliveryAddresses()}
                                        selected={deliveryAddressId}
                                        onChange={(event) =>
                                            setDeliveryAddressId(event.target.value)
                                        }
                                    />
                                </div>
                                <div className="mb-5">
                                    <TextArea
                                        label=""
                                        value={getDeliveryAddressDetails()}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        <div />
                        <div className="flex mb-5 text-sm text-danger">{sendOrderErrorMessage}</div>
                        <div className="flex flex-row-reverse">
                            <div className="pl-2" />
                            <div>
                                <Button onClick={postSendOrder} disabled={isLoading}>
                                    {!isLoading ? 'Submit' : <div>Loading</div>}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                    <div className="bg-surface border border-border rounded-xl h-full mx-4 my-4">
                        {renderOrder()}
                    </div>
                    <div className="bg-surface border border-border rounded-xl h-full mx-4 my-4">
                        <div className="p-5">
                            <TabsContainer
                                selectedTab={selectedTab}
                                options={tabs}
                                onClick={setSelectedTab}
                            >
                                {selectedTab === 'Materials' && (
                                    <div className="w-full h-full mt-4">
                                        <table className="table-auto bg-surface w-full">
                                            <thead>
                                                <TableRow>
                                                    <TableHeader>No</TableHeader>
                                                    <TableHeader width="w-1/3">
                                                        Description
                                                    </TableHeader>
                                                    <TableHeader>Trade Code</TableHeader>
                                                    <TableHeader>Cost Code</TableHeader>
                                                    <TableHeader>Creditor</TableHeader>
                                                    <TableHeader>Unit</TableHeader>
                                                    <TableHeader width="w-6">
                                                        Quantity Remaining
                                                    </TableHeader>
                                                    <TableHeader width="w-6">
                                                        Quantity (Initial)
                                                    </TableHeader>
                                                    <TableHeader>Buy Out Rate</TableHeader>
                                                    <TableHeader>Amount</TableHeader>
                                                </TableRow>
                                            </thead>
                                            <tbody>
                                                {order.data &&
                                                    order.data.lineItems &&
                                                    order.data.lineItems.map(
                                                        (reqItem: RequisitionItem) => (
                                                            <TableRow hover>
                                                                <TableData>
                                                                    {getLineItemNumber(reqItem)}
                                                                </TableData>
                                                                <TableData id={reqItem.id}>
                                                                    {reqItem.description}
                                                                </TableData>
                                                                <TableData>
                                                                    {`${reqItem.costCode?.trade?.description}`}
                                                                </TableData>
                                                                <TableData>{`${reqItem.costCode?.code} - ${reqItem.costCode?.name}`}</TableData>
                                                                <TableData>
                                                                    {reqItem.creditor?.name || ''}
                                                                </TableData>
                                                                <TableData>
                                                                    {reqItem.unit?.description ||
                                                                        ''}
                                                                </TableData>
                                                                <TableData>
                                                                    {order.data?.type === 'BULK' &&
                                                                        reqItem.quantity &&
                                                                        round(
                                                                            reqItem.quantity -
                                                                                getTotalCalledOff(
                                                                                    reqItem,
                                                                                    order.data
                                                                                ),
                                                                            DECIMAL_PLACES
                                                                        )}
                                                                    {order.data?.type ===
                                                                        'SINGLE' &&
                                                                        reqItem.quantity &&
                                                                        round(
                                                                            reqItem.quantity -
                                                                                getTotalDelivered(
                                                                                    reqItem,
                                                                                    order.data
                                                                                ),
                                                                            DECIMAL_PLACES
                                                                        )}
                                                                </TableData>
                                                                <TableData>
                                                                    {reqItem.quantity}
                                                                </TableData>
                                                                <TableData>
                                                                    <div className="flex justify-between">
                                                                        <span>
                                                                            {fomatCurrencySymbol(
                                                                                reqItem.currency ||
                                                                                    ''
                                                                            )}
                                                                        </span>
                                                                        <span>
                                                                            {formatCurrencyAmount(
                                                                                reqItem.buyOutRate ||
                                                                                    0
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </TableData>
                                                                <TableData>
                                                                    <div className="flex justify-between">
                                                                        <span>
                                                                            {fomatCurrencySymbol(
                                                                                reqItem.currency ||
                                                                                    ''
                                                                            )}
                                                                        </span>
                                                                        <span>
                                                                            {formatCurrencyAmount(
                                                                                (reqItem.quantity ||
                                                                                    0) *
                                                                                    (reqItem.buyOutRate ||
                                                                                        0)
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </TableData>
                                                            </TableRow>
                                                        )
                                                    )}
                                                {order.data && order.data.lineItems && (
                                                    <>
                                                        <tr className="text-xs font-bold">
                                                            <td colSpan={8} />
                                                            <td className="border border-border p-1 w-36 text-foreground">
                                                                Sub Total Amount
                                                            </td>
                                                            <td className="border border-border p-1 flex justify-between text-foreground">
                                                                <span>
                                                                    {fomatCurrencySymbol(
                                                                        order.data.currency || ''
                                                                    )}
                                                                </span>
                                                                <span>
                                                                    {getOrderTotal(
                                                                        order.data.lineItems || []
                                                                    )}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                        <tr className="text-xs font-bold">
                                                            <td colSpan={8} />
                                                            <td className="border border-border p-1 text-foreground">
                                                                VAT Amount
                                                            </td>
                                                            <td className="border border-border p-1 flex justify-between text-foreground">
                                                                <span>
                                                                    {fomatCurrencySymbol(
                                                                        order.data.currency || ''
                                                                    )}
                                                                </span>
                                                                <span>
                                                                    {formatCurrencyAmount(
                                                                        getVatTotal(
                                                                            order.data.lineItems ||
                                                                                []
                                                                        )
                                                                    )}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                        <tr className="text-xs font-bold">
                                                            <td colSpan={8} />
                                                            <td className="border border-border p-1 text-foreground">
                                                                Total Amount
                                                            </td>
                                                            <td className="border border-border p-1 flex justify-between text-foreground">
                                                                <span>
                                                                    {fomatCurrencySymbol(
                                                                        order.data.currency || ''
                                                                    )}
                                                                </span>
                                                                <span>
                                                                    {getTotalAmount(
                                                                        getOrderRawTotal(
                                                                            order.data.lineItems
                                                                        ),
                                                                        getVatTotal(
                                                                            order.data.lineItems ||
                                                                                []
                                                                        )
                                                                    )}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {selectedTab === 'Deliveries' && (
                                    <div className="space-y-5 pt-5">
                                        {user &&
                                            order.data &&
                                            order.data.cancelledAt === null &&
                                            order.data.cancelledBy === null &&
                                            order.data.approvedAt !== null &&
                                            order.data.approvedBy !== null &&
                                            getPermissions.data?.items.find(
                                                (p) => p.code === 'Order_Create_Delivery'
                                            )?.hasAccess && (
                                                <div className="flex">
                                                    <Button
                                                        onClick={() =>
                                                            handleCreateDeliveryModalOpen()
                                                        }
                                                    >
                                                        Add Delivery
                                                    </Button>
                                                </div>
                                            )}
                                        <div className="text-l font-bold">Delivery History</div>
                                        {order.data && order.data.orderDeliveries && (
                                            <Table<Delivery>
                                                data={order.data.orderDeliveries}
                                                onClick={(i) => toDeliveryDetail(i.id)}
                                                columns={[
                                                    {
                                                        key: 'note',
                                                        title: 'Delivery Note',
                                                        render: (delivery) => `${delivery.note}`,
                                                    },
                                                    {
                                                        key: 'deliveredAt',
                                                        title: 'Delivered At',
                                                        render: (delivery) =>
                                                            delivery.deliveredAt &&
                                                            new Date(
                                                                delivery.deliveredAt
                                                            ).toLocaleDateString('en-ZA'),
                                                    },
                                                ]}
                                            />
                                        )}
                                    </div>
                                )}
                                {selectedTab === 'Call-Offs' && (
                                    <div className="space-y-5 pt-5">
                                        {/* Calloffs can be added by level 3 and higher + buyers at any time. level 1 & 2 can only add if not approved */}
                                        {order.data &&
                                            order.data.cancelledAt === null &&
                                            order.data.cancelledBy === null &&
                                            ((order.data.approvedAt !== null &&
                                                order.data.approvedBy !== null &&
                                                getPermissions.data?.items.find(
                                                    (p) =>
                                                        p.code === 'Order_Create_CallOff_IfApproved'
                                                )?.hasAccess) ||
                                                getPermissions.data?.items.find(
                                                    (p) => p.code === 'Order_Create_CallOff'
                                                )?.hasAccess) && (
                                                <div className="flex flex-row-reverse">
                                                    <Button
                                                        onClick={() =>
                                                            handleCreateCallOffModalOpen()
                                                        }
                                                    >
                                                        Add Call-Off
                                                    </Button>
                                                </div>
                                            )}
                                        <div className="text-l">Call-Off History</div>
                                        {order.data && order.data.callOffs && (
                                            <Table<CallOff>
                                                data={order.data.callOffs}
                                                onClick={(i) => toCallOffDetail(i.id)}
                                                columns={[
                                                    {
                                                        key: 'number',
                                                        title: 'Number',
                                                        render: (callOff) => `${callOff.number}`,
                                                    },
                                                    {
                                                        key: 'description',
                                                        title: 'Call-Off Note',
                                                        render: (callOff) =>
                                                            `${callOff.description}`,
                                                    },
                                                    {
                                                        key: 'requiredAt',
                                                        title: 'Required At',
                                                        render: (callOff) =>
                                                            callOff.requiredAt &&
                                                            new Date(
                                                                callOff.requiredAt
                                                            ).toLocaleDateString('en-ZA'),
                                                    },
                                                ]}
                                            />
                                        )}
                                    </div>
                                )}
                            </TabsContainer>
                        </div>
                        {renderOrderButtons()}
                        {errorMessage && (
                            <div className="flex p-5 text-sm text-danger">{errorMessage}</div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
